import axios from '../lib/axios';

export interface MerchantStats {
  totalMerchants: number;
  activeMerchants: {
    count: number;
    percentage: string;
  };
  inactiveMerchants: {
    count: number;
    percentage: string;
  };
  kycPendingMerchants: {
    count: number;
    percentage: string;
  };
}

export interface PartnerStats {
  totalPartners: number;
  evPartners: {
    count: number;
    percentage: string;
  };
  bnplPartners: {
    count: number;
    percentage: string;
  };
}

export interface CompletedOrderStats {
  totalOrders: number;
  completedOrdersCount: number;
  completedTodayCount: number;
  completedThisWeekCount: number;
  completedThisMonthCount: number;
  conversion: {
    rate: string;
    basedOn: string;
  };
}

export interface CustomerStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
}

export interface PreApprovalStats {
  today: {
    cashe: {
      approved: {
        count: number;
        percentage: string;
      };
      rejected: {
        count: number;
        percentage: string;
      };
    };
    zype: {
      offerExists: {
        count: number;
        percentage: string;
      };
      noOffer: {
        count: number;
        percentage: string;
      };
    };
  };
  thisWeek: {
    cashe: {
      approved: {
        count: number;
        percentage: string;
      };
      rejected: {
        count: number;
        percentage: string;
      };
    };
    zype: {
      offerExists: {
        count: number;
        percentage: string;
      };
      noOffer: {
        count: number;
        percentage: string;
      };
    };
  };
  thisMonth: {
    cashe: {
      approved: {
        count: number;
        percentage: string;
      };
      rejected: {
        count: number;
        percentage: string;
      };
    };
    zype: {
      offerExists: {
        count: number;
        percentage: string;
      };
      noOffer: {
        count: number;
        percentage: string;
      };
    };
  };
}

export interface MerchantByLenderStats {
  totalMerchants: number;
  breakdownByLender: {
    lender: string;
    count: number;
    percentage: number;
  }[];
}

export interface OrderStatusStats {
  today: Record<string, number>;
  thisWeek: {
    start: string;
    end: string;
    stats: Record<string, number>;
  };
  thisMonth: {
    start: string;
    end: string;
    stats: Record<string, number>;
  };
  lastSixMonths: {
    start: string;
    end: string;
    [key: string]: string | number;
  };
}

export interface MerchantKycStatusStats {
  totalMerchants: number;
  kycCompleted: number;
  kycPending: number;
}

/**
 * Fetch merchant statistics for dashboard
 */
export const getMerchantStats = async (): Promise<MerchantStats> => {
  try {
    const response = await axios.get('/dashboard/merchant-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching merchant stats:', error);
    throw error;
  }
};

/**
 * Fetch partner statistics for dashboard
 */
export const getPartnerStats = async (): Promise<PartnerStats> => {
  try {
    const response = await axios.get('/dashboard/partner-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching partner stats:', error);
    throw error;
  }
};

/**
 * Fetch completed order statistics for dashboard
 */
export const getCompletedOrderStats = async (): Promise<CompletedOrderStats> => {
  try {
    const response = await axios.get('/dashboard/completed-order-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching completed order stats:', error);
    throw error;
  }
};

/**
 * Fetch customer statistics for dashboard
 */
export const getCustomerStats = async (): Promise<CustomerStats> => {
  try {
    const response = await axios.get('/dashboard/customer-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching customer stats:', error);
    throw error;
  }
};

/**
 * Fetch pre-approval statistics for dashboard
 */
export const getPreApprovalStats = async (): Promise<PreApprovalStats> => {
  try {
    const response = await axios.get('/dashboard/pre-approvals-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching pre-approval stats:', error);
    throw error;
  }
};

/**
 * Fetch merchant by lender statistics for dashboard
 */
export const getMerchantByLenderStats = async (): Promise<MerchantByLenderStats> => {
  try {
    const response = await axios.get('/dashboard/merchant-by-lender-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching merchant by lender stats:', error);
    throw error;
  }
};

/**
 * Fetch order status statistics for dashboard
 */
export const getOrderStatusStats = async (): Promise<OrderStatusStats> => {
  try {
    const response = await axios.get('/dashboard/order-status-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching order status stats:', error);
    throw error;
  }
};

/**
 * Fetch merchant KYC status statistics for dashboard
 */
export const getMerchantKycStatusStats = async (): Promise<MerchantKycStatusStats> => {
  try {
    const response = await axios.get('/dashboard/merchant-kyc-status-stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching merchant KYC status stats:', error);
    throw error;
  }
};

/**
 * Fetch all dashboard statistics in a single call
 */
export const getAllDashboardStats = async () => {
  try {
    const [
      merchantStats, 
      partnerStats, 
      orderStats, 
      customerStats, 
      preApprovalStats, 
      merchantByLenderStats,
      orderStatusStats,
      merchantKycStatusStats
    ] = await Promise.all([
      getMerchantStats(),
      getPartnerStats(),
      getCompletedOrderStats(),
      getCustomerStats(),
      getPreApprovalStats(),
      getMerchantByLenderStats(),
      getOrderStatusStats(),
      getMerchantKycStatusStats()
    ]);

    return {
      merchantStats,
      partnerStats,
      orderStats,
      customerStats,
      preApprovalStats,
      merchantByLenderStats,
      orderStatusStats,
      merchantKycStatusStats
    };
  } catch (error) {
    console.error('Error fetching all dashboard stats:', error);
    throw error;
  }
}; 